import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { getTokenSilently } from '@/utils/auth';
import {
  ApprovalCardData,
  ApprovalDecision,
  ApprovalStatus,
  ChatContext,
  ChatMessage,
  ChatProcessingState,
  ChatToolStatus,
  ChatState,
  ChatActions,
  UseChatAgentReturn,
  ChatAgentConfig,
  HistoryMessage,
  MessageRequestPayload,
  MessageResponsePayload,
  SessionCreatePayload,
  SessionCreateResponse,
  SessionEventData,
  MessageSavedEventData,
} from '@/lib/types/chat';
import { useUserRole } from '@/lib/rbac';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { CURRENT_WORKSPACE_ID } from '@/utils/local_storage_keys';
import { localeHeader } from '@/lib/i18n/headers';

const AGENT_BASE_URL = import.meta.env.VITE_ASK_AI_AGENT_BASE_URL || 'https://csv-import.dev.coffee.work/agent';
const DEFAULT_MESSAGE_ENDPOINT = `${AGENT_BASE_URL}/messages`;
const DEFAULT_STREAM_ENDPOINT = `${AGENT_BASE_URL}/stream`;
const DEFAULT_SESSION_ENDPOINT = `${AGENT_BASE_URL}/sessions`;
const DEFAULT_APPROVAL_ENDPOINT = `${AGENT_BASE_URL}/approvals`;

// Declared at session creation; the agent gates writes only for clients that render cards.
const APPROVAL_CARDS_CAPABILITY = 'approval_cards';

// Read at request time (not render time) so the agent always receives the
// workspace the user is in *now*. Without this header the agent backend falls
// back to the server-side `lastWorkspaceId`, which does not track client-side
// workspace switches — queries then run (and conversations get persisted) in
// the wrong workspace.
const workspaceHeader = (): Record<string, string> => {
  const id = typeof window !== 'undefined' ? localStorage.getItem(CURRENT_WORKSPACE_ID) : null;
  return id ? { 'x-workspace-id': id } : {};
};

// Ensure a space between streamed chunks after sentence-ending punctuation.
const appendChunk = (prev: string, chunk: string): string => {
  if (prev && chunk && !prev.endsWith(' ') && !chunk.startsWith(' ')) {
    const lastChar = prev[prev.length - 1];
    const firstChar = chunk[0];
    if (/[.!?]/.test(lastChar) && /[A-Z]/.test(firstChar)) {
      return prev + ' ' + chunk;
    }
  }
  return prev + chunk;
};

export function useChatAgent(config?: ChatAgentConfig): UseChatAgentReturn {
  const {
    messageEndpoint = DEFAULT_MESSAGE_ENDPOINT,
    streamEndpoint = DEFAULT_STREAM_ENDPOINT,
    sessionEndpoint = DEFAULT_SESSION_ENDPOINT,
    approvalEndpoint = DEFAULT_APPROVAL_ENDPOINT,
    autoConnect = false,
    initializeSession = false,
    initialContext,
    approvalCards = false,
  } = config || {};

  const userRole = useUserRole();
  const workspaceId = useWorkspaceId();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentResponse, setCurrentResponse] = useState('');
  const [processingState, setProcessingState] = useState<ChatProcessingState>('idle');
  const [toolStatus, setToolStatus] = useState<ChatToolStatus>({
    isActive: false,
    isComplete: false,
  });
  // tool_call_id -> raw tool name, for every tool currently running.
  // Keyed by id so parallel calls (and repeat calls of the same tool)
  // are tracked independently and the indicator only clears when the
  // last one finishes — not on the first finish (the old bug).
  const activeToolsRef = useRef<Map<string, string>>(new Map());

  const syncToolStatus = useCallback(() => {
    const names = Array.from(activeToolsRef.current.values());
    setToolStatus((prev) => ({
      ...prev,
      isActive: names.length > 0,
      isComplete: names.length === 0,
      activeTools: names,
      toolName: names[0],
    }));
  }, []);

  // SSE event data is "name|id" (see langgraph_service.py). Tolerates a
  // bare name (no pipe) defensively.
  const handleToolStart = useCallback((data: string) => {
    const [name, id = name] = data.split('|');
    activeToolsRef.current.set(id, name);
    setProcessingState('processing');
    syncToolStatus();
  }, [syncToolStatus]);

  const handleToolFinish = useCallback((data: string) => {
    const [name, id = name] = data.split('|');
    activeToolsRef.current.delete(id);
    syncToolStatus();
  }, [syncToolStatus]);

  // Holds the persisted assistant message id from the `message_saved` SSE event.
  // It arrives before `done`, so we stash it and attach it when `done` builds the
  // assistant ChatMessage. Reset per message in sendMessage().
  const pendingBackendMessageIdRef = useRef<number | null>(null);

  const handleMessageSaved = useCallback((data: string) => {
    try {
      const parsed: MessageSavedEventData = JSON.parse(data);
      pendingBackendMessageIdRef.current = parsed.message_id ?? null;
    } catch (err) {
      console.error('Error parsing message_saved data:', err);
    }
  }, []);
  const [sessionId, setSessionId] = useState('');
  const [context, setContext] = useState<ChatContext | null>(initialContext ?? null);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const messageAddedRef = useRef<boolean>(false);
  const isCreatingSessionRef = useRef<boolean>(false);
  const queuedMessageRef = useRef<string | null>(null);
  const resumeHistoryRef = useRef<HistoryMessage[] | null>(null);
  const resumeConversationIdRef = useRef<number | null>(null);
  const resumeUiMessagesRef = useRef<ChatMessage[] | null>(null);

  const setApprovalStatus = useCallback((approvalId: string, status: ApprovalStatus) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.approval?.card.approval_id === approvalId ? { ...m, approval: { ...m.approval, status } } : m,
      ),
    );
  }, []);

  // The turn paused before writing: show the card and wait for the user. The
  // held calls' indicators were already cleared by the agent.
  const handleApprovalRequired = useCallback((data: string) => {
    try {
      const card: ApprovalCardData = JSON.parse(data);
      setMessages((prev) => [
        ...prev,
        {
          id: `approval-${card.approval_id}`,
          content: '',
          isUser: false,
          timestamp: new Date(),
          approval: { card, status: 'pending' },
        },
      ]);
      activeToolsRef.current.clear();
      setToolStatus({ isActive: false, isComplete: false });
      setProcessingState('awaiting_approval');
    } catch (err) {
      console.error('Error parsing approval_required data:', err);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // The one place a session's stream is opened and its events handled.
  const openStream = useCallback((id: string) => {
    const eventSource = new EventSource(`${streamEndpoint}/${id}`);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsConnected(true);
      setError(null);
    };

    eventSource.addEventListener('session', (event) => {
      try {
        const data: SessionEventData = JSON.parse(event.data);
        setSessionId((current) => current || data.session_id);
      } catch (err) {
        console.error('Error parsing session data:', err);
      }
    });

    eventSource.addEventListener('message', (event) => {
      setProcessingState('streaming');
      setToolStatus({ isActive: false, isComplete: false });
      setCurrentResponse((prev) => appendChunk(prev, event.data));
    });

    eventSource.addEventListener('tool_usage_started', (event) => {
      handleToolStart(event.data);
    });

    eventSource.addEventListener('tool_usage_finished', (event) => {
      handleToolFinish(event.data);
    });

    eventSource.addEventListener('thinking', () => {
      setProcessingState('processing');
    });

    eventSource.addEventListener('web_search_used', () => {
      setToolStatus((prev) => ({
        ...prev,
        webSearchUsed: true,
      }));
    });

    eventSource.addEventListener('message_saved', (event) => {
      handleMessageSaved(event.data);
    });

    eventSource.addEventListener('approval_required', (event) => {
      handleApprovalRequired(event.data);
    });

    eventSource.addEventListener('done', () => {
      const backendMessageId = pendingBackendMessageIdRef.current ?? undefined;
      pendingBackendMessageIdRef.current = null;
      setCurrentResponse((finalResponse) => {
        if (finalResponse.trim() && !messageAddedRef.current) {
          messageAddedRef.current = true;
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now().toString(),
              content: finalResponse.trim(),
              isUser: false,
              timestamp: new Date(),
              backendMessageId,
            },
          ]);
        }
        return '';
      });
      // A paused turn also ends with `done`; it is still waiting on the card.
      setProcessingState((prev) => (prev === 'awaiting_approval' ? prev : 'idle'));
      setToolStatus({ isActive: false, isComplete: false });
    });

    eventSource.addEventListener('keepalive', () => {
    });

    eventSource.addEventListener('error', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        console.error('SSE error event:', data);
        setError(data.message || 'An error occurred');
      } catch (err) {
        console.error('Error parsing error event data:', err);
        setError('An error occurred');
      }
      setProcessingState('idle');
      setToolStatus({ isActive: false, isComplete: false });
      setCurrentResponse('');
    });

    eventSource.onerror = (err) => {
      console.error('SSE connection error:', err);
      setProcessingState('idle');
      setToolStatus({ isActive: false, isComplete: false });
      setError('Connection error occurred');
      setIsConnected(false);
    };

    return eventSource;
  }, [streamEndpoint, handleToolStart, handleToolFinish, handleMessageSaved, handleApprovalRequired]);

  const createSession = useCallback(async () => {
    if (isCreatingSessionRef.current || sessionId) {
      return;
    }

    isCreatingSessionRef.current = true;

    try {
      const token = await getTokenSilently();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload: SessionCreatePayload = {
        context: context && context.type !== 'general' ? context : undefined,
        user_role: userRole,
      };
      if (approvalCards) {
        payload.client_capabilities = [APPROVAL_CARDS_CAPABILITY];
      }

      if (resumeHistoryRef.current) {
        payload.conversation_history = resumeHistoryRef.current;
        resumeHistoryRef.current = null;
      }
      if (resumeConversationIdRef.current !== null) {
        payload.existing_conversation_id = resumeConversationIdRef.current;
        resumeConversationIdRef.current = null;
      }

      console.log('[createSession] Creating session with context:', context);
      console.log('[createSession] Payload:', JSON.stringify(payload));

      const response = await fetch(sessionEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Timezone': timezone,
          ...localeHeader(),
          ...workspaceHeader(),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[createSession] HTTP error:', response.status, response.statusText);
        console.error('[createSession] Response body:', errorText);
        throw new Error(`Failed to create session: ${response.status} ${errorText}`);
      }

      const result: SessionCreateResponse = await response.json();
      setSessionId(result.session_id);

      if (resumeUiMessagesRef.current) {
        setMessages(resumeUiMessagesRef.current);
        resumeUiMessagesRef.current = null;
      }

      openStream(result.session_id);
    } catch (err) {
      console.error('[createSession] Error creating session:', err);
      setError('Failed to create session');
    } finally {
      isCreatingSessionRef.current = false;
    }
  }, [sessionId, context, sessionEndpoint, userRole, approvalCards, openStream]);

  const connect = useCallback(() => {
    if (!sessionId) {
      console.warn('Cannot connect: no session ID');
      return;
    }

    if (eventSourceRef.current) {
      return;
    }

    openStream(sessionId);
  }, [sessionId, openStream]);

  const sendMessage = useCallback(
    async (messageText: string, isFromQueue: boolean = false) => {
      if (!messageText.trim()) return;

      // If session is still being created, queue the message
      if (isCreatingSessionRef.current) {
        queuedMessageRef.current = messageText.trim();

        // Add user message to UI immediately
        const userMessage: ChatMessage = {
          id: Date.now().toString(),
          content: messageText.trim(),
          isUser: true,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);
        setProcessingState('processing');
        activeToolsRef.current.clear();
        setToolStatus({ isActive: true, isComplete: false });
        return;
      }

      // Typing instead of deciding is allowed: the agent drops the pending card
      // and nothing it proposed is written.
      const awaitingApproval = processingState === 'awaiting_approval';
      if (!isFromQueue && processingState !== 'idle' && !awaitingApproval) return;
      if (awaitingApproval) {
        setMessages((prev) =>
          prev.map((m) =>
            m.approval?.status === 'pending' ? { ...m, approval: { ...m.approval, status: 'superseded' } } : m,
          ),
        );
      }

      messageAddedRef.current = false;
      pendingBackendMessageIdRef.current = null;

      // Only add user message to UI if not from queue (already added)
      if (!isFromQueue) {
        const userMessage: ChatMessage = {
          id: Date.now().toString(),
          content: messageText.trim(),
          isUser: true,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);
      }

      setProcessingState('processing');
      activeToolsRef.current.clear();
      setToolStatus({ isActive: true, isComplete: false });
      setError(null);

      try {
        const token = await getTokenSilently();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const payload: MessageRequestPayload = {
          session_id: sessionId || undefined,
          text: messageText.trim(),
        };

        const response = await fetch(messageEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Timezone': timezone,
            ...localeHeader(),
            ...workspaceHeader(),
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result: MessageResponsePayload = await response.json();

        if (!sessionId && result.session_id) {
          setSessionId(result.session_id);
        }

        if (!eventSourceRef.current && result.session_id) {
          openStream(result.session_id);
        }
      } catch (err) {
        console.error('Error sending message:', err);
        setProcessingState('idle');
        setToolStatus({ isActive: false, isComplete: false });
        setError('Failed to send message');
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            content: 'Sorry, there was an error sending your message. Please try again.',
            isUser: false,
            timestamp: new Date(),
          },
        ]);
      }
    },
    [sessionId, processingState, messageEndpoint, openStream]
  );

  // Approve or decline the pending card. The resumed turn's events arrive on the
  // open stream like any other turn, so this only sends the decision.
  const respondToApproval = useCallback(
    async (approvalId: string, decision: ApprovalDecision) => {
      setApprovalStatus(approvalId, decision);
      messageAddedRef.current = false;
      pendingBackendMessageIdRef.current = null;
      setProcessingState('processing');
      setToolStatus({ isActive: true, isComplete: false });
      setError(null);

      try {
        const token = await getTokenSilently();
        const response = await fetch(approvalEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...localeHeader(),
            ...workspaceHeader(),
          },
          body: JSON.stringify({ session_id: sessionId, approval_id: approvalId, decision }),
        });

        // Gone or unknown: the agent restarted, the session was released, or the card
        // timed out. Nothing was written either way.
        if (response.status === 404 || response.status === 410) {
          setApprovalStatus(approvalId, 'expired');
          setProcessingState('idle');
          setToolStatus({ isActive: false, isComplete: false });
          return;
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (err) {
        console.error('Error sending approval decision:', err);
        setApprovalStatus(approvalId, 'pending');
        setProcessingState('awaiting_approval');
        setToolStatus({ isActive: false, isComplete: false });
        setError('Failed to send approval decision');
      }
    },
    [sessionId, approvalEndpoint, setApprovalStatus]
  );

  const reset = useCallback(() => {
    disconnect();
    setMessages([]);
    setCurrentResponse('');
    setProcessingState('idle');
    setToolStatus({ isActive: false, isComplete: false });
    setSessionId('');
    setError(null);
  }, [disconnect]);

  const updateContext = useCallback((newContext: ChatContext) => {
    setContext(newContext);
  }, []);

  const resumeConversation = useCallback(
    (uiMessages: ChatMessage[], historyPayload: HistoryMessage[], conversationId: number) => {
      // Set refs before reset() so they're available when createSession fires on next render
      resumeHistoryRef.current = historyPayload;
      resumeConversationIdRef.current = conversationId;
      resumeUiMessagesRef.current = uiMessages;
      reset(); // clears sessionId → triggers createSession() via useEffect
    },
    [reset]
  );

  useEffect(() => {
    if (autoConnect && sessionId && !eventSourceRef.current) {
      connect();
    }
  }, [autoConnect, sessionId, connect]);

  // A session is created (and its conversation persisted) under one workspace.
  // The panel hosting this hook survives workspace switches (it's mounted
  // outside WorkspaceRemount), so drop the session and messages when the
  // workspace changes — continuing the old session would mix workspace A's
  // conversation into workspace B. Skip the initial null → id transition on
  // first login so we don't reset a session that was just created.
  const prevWorkspaceIdRef = useRef<string | null>(workspaceId);
  useEffect(() => {
    const prev = prevWorkspaceIdRef.current;
    prevWorkspaceIdRef.current = workspaceId;
    if (prev && workspaceId && prev !== workspaceId) {
      reset();
    }
  }, [workspaceId, reset]);

  // Sync context state with initialContext prop
  // When context changes (e.g., switching meetings), reset session to create new one with updated context
  // Use JSON.stringify to compare context by value, not reference
  const initialContextKey = initialContext ? JSON.stringify(initialContext) : null;
  const currentContextKey = context ? JSON.stringify(context) : null;

  useEffect(() => {
    if (initialContext) {
      const contextChanged = context && (
        context.type !== initialContext.type ||
        context.id !== initialContext.id
      );

      if (contextChanged && sessionId) {
        console.log('[useChatAgent] Context changed, resetting session:', {
          old: context,
          new: initialContext,
        });
        reset();
      }

      setContext(initialContext);
    }
  }, [initialContextKey, currentContextKey, sessionId, reset]);

  // Create session when initializeSession is true and we have context
  // Use JSON.stringify to compare context by value, not reference
  const contextKey = context ? JSON.stringify(context) : null;
  useEffect(() => {
    if (initializeSession && !sessionId && !isCreatingSessionRef.current && context) {
      createSession();
    }
  }, [initializeSession, sessionId, createSession, contextKey]);

  useEffect(() => {
    // Send queued message when session becomes ready
    if (sessionId && queuedMessageRef.current && !isCreatingSessionRef.current) {
      const queuedMsg = queuedMessageRef.current;
      queuedMessageRef.current = null;
      sendMessage(queuedMsg, true); // Pass true to indicate this is from queue
    }
  }, [sessionId, sendMessage]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const state: ChatState = {
    messages,
    currentResponse,
    processingState,
    toolStatus,
    sessionId,
    context,
    error,
    isConnected,
  };

  const actions: ChatActions = useMemo(() => ({
    sendMessage,
    reset,
    connect,
    disconnect,
    updateContext,
    resumeConversation,
    respondToApproval,
  }), [sendMessage, reset, connect, disconnect, updateContext, resumeConversation, respondToApproval]);

  return {
    state,
    actions,
    isReady: processingState === 'idle' || processingState === 'awaiting_approval',
  };
}
